import { useQuery } from '@tanstack/react-query';
import { computeSpotRate, type SpotRateInput, type SpotRateResult } from '@/lib/spotRateEngine';
import {
  buildSpotRateKey,
  SPOT_RATE_STALE_MS,
  SPOT_RATE_GC_MS,
} from '@/lib/spotRateTelemetry';

interface UseSpotRateParams {
  originLat: number | null | undefined;
  originLon: number | null | undefined;
  destLat: number | null | undefined;
  destLon: number | null | undefined;
  laneMiles: number | null | undefined;
  equipmentType: string | null | undefined;
  /** Optional — enables L4 (daily baseline) fallback. */
  originCity?: string | null;
  originState?: string | null;
  destCity?: string | null;
  destState?: string | null;
  /** Load IDs to exclude from comp pool (e.g. user's own loads) */
  excludeLoadIds?: string[];
  enabled?: boolean;
}

export function useSpotRate(params: UseSpotRateParams) {
  const {
    originLat, originLon, destLat, destLon, laneMiles, equipmentType,
    originCity, originState, destCity, destState,
    excludeLoadIds, enabled = true,
  } = params;

  const hasAll = !!(
    originLat != null && originLon != null &&
    destLat != null && destLon != null &&
    laneMiles && laneMiles > 0 &&
    equipmentType
  );

  const input: SpotRateInput | null = hasAll ? {
    originLat: originLat!,
    originLon: originLon!,
    destLat: destLat!,
    destLon: destLon!,
    laneMiles: laneMiles!,
    equipmentType: equipmentType!,
    originCity: originCity ?? undefined,
    originState: originState ?? undefined,
    destCity: destCity ?? undefined,
    destState: destState ?? undefined,
    excludeLoadIds,
  } : null;

  return useQuery<SpotRateResult | null>({
    queryKey: input
      ? buildSpotRateKey(input)
      : ['spot-rate', 'disabled'],
    queryFn: () => computeSpotRate(input!),
    enabled: enabled && hasAll,
    staleTime: SPOT_RATE_STALE_MS,
    gcTime: SPOT_RATE_GC_MS,
    retry: 1,
  });
}
